<script setup lang="ts">
import { onMounted, ref } from 'vue';

import { capitalizeFirstLetter } from '@/utils/text';
import { useRoute } from 'vue-router';

import InformationBox from '@/components/InformationBox.vue';
import Link from '@/components/Link.vue';
import List from '@/components/List.vue';
import { OrganizationRole } from '@/enums/roles';
import { useAuthStore } from '@/stores/auth';
import { useGlobalStore } from '@/stores/global';
import { useTeamStore } from '@/stores/team';
import type { Employee } from '@/types/employee';
import type { Team } from '@/types/team';
import toast from '@/utils/toast';
import type { DataTableHeader } from 'vuetify';

const route = useRoute();
const teamIdFromUrl = route.params.id as string;

const teamStore = useTeamStore();
const authStore = useAuthStore();
const globalStore = useGlobalStore();

const team = ref<Team | null>(null);
const isInitializing = ref(true);

if (!teamIdFromUrl) toast.error('TeamRequired');

onMounted(async () => {
    team.value = await teamStore.fetchById(teamIdFromUrl);
    isInitializing.value = false;
});

const tableHeaders: DataTableHeader[] = [{ title: 'Naam', value: 'fullName', sortable: true }];

function currentEmployeeCanUseActions() {
    return authStore.isAuthorized([OrganizationRole.Admin], authStore.getCurrentOrganization());
}

async function removeEmployeeFromTeam(employee: Employee): Promise<void> {
    if (!team.value) {
        toast.error('TeamRequired');
        return;
    }

    await teamStore.removeEmployeeFromTeam(team.value._id, employee._id);
}
</script>

<template>
    <v-fade-transition>
        <div v-show="!isInitializing && !globalStore.isLoading">
            <template v-if="!team">
                <p>Team niet gevonden</p>
            </template>

            <template v-else>
                <InformationBox
                    class="my-8"
                    :fields="[
                        { title: 'Naam', value: capitalizeFirstLetter(team.name) },
                        { title: 'Teamleider', value: team.teamleader ? team.teamleader.fullName : '-' }
                    ]"
                />

                <List
                    title="Medewerkers"
                    entity-name="Medewerker"
                    name-property="fullName"
                    :items-per-page="15"
                    fixed-header
                    no-data-text="Dit team bevat nog geen medewerkers"
                    :headers="tableHeaders"
                    :items="team.employees"
                    :is-deletable="currentEmployeeCanUseActions()"
                    @delete-confirmed="removeEmployeeFromTeam"
                >
                    <template v-slot:item.fullName="{ item }">
                        <Link :route-to="{ name: 'employee', params: { id: item._id } }" icon="person" :displayText="item.fullName" />
                    </template>
                </List>
            </template>
        </div>
    </v-fade-transition>
</template>
